import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { submitQuote, SERVICES } from "@/lib/api";

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: SERVICES[0],
  location: "",
  message: "",
};

const Field = ({ label, name, value, onChange, type = "text", testid, required }) => (
  <div className="flex flex-col">
    <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
      {label} {required && <span className="text-primary">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="bg-transparent border-0 border-b border-border py-3 text-foreground font-plex text-lg outline-none focus:border-primary transition-colors duration-300"
    />
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitQuote(form);
      setDone(true);
      setForm(initial);
      toast.success("Quote request sent. We'll be in touch shortly.");
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-background border-t border-border grid lg:grid-cols-2"
    >
      {/* left: contact info */}
      <div className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b lg:border-b-0 lg:border-r border-border">
        <span className="font-mono text-xs uppercase tracking-widest text-primary">
          [ 05 — Get in Touch ]
        </span>
        <h2 className="font-bebas uppercase leading-[0.85] text-foreground text-6xl md:text-8xl mt-4">
          Let&apos;s Build
          <br />
          Together
        </h2>

        <div className="mt-14 space-y-10">
          <div>
            <div className="flex items-center gap-3 text-muted-foreground font-mono text-[10px] uppercase tracking-widest mb-3">
              <Phone size={14} className="text-primary" /> Call Us
            </div>
            <a
              href="tel:+919810376040"
              data-testid="phone-1"
              className="block font-bebas text-4xl md:text-5xl text-foreground hover:text-primary transition-colors duration-300"
            >
              98103 76040
            </a>
            <a
              href="tel:+919582741576"
              data-testid="phone-2"
              className="block font-bebas text-4xl md:text-5xl text-foreground hover:text-primary transition-colors duration-300 mt-1"
            >
              95827 41576
            </a>
          </div>

          <div>
            <div className="flex items-center gap-3 text-muted-foreground font-mono text-[10px] uppercase tracking-widest mb-3">
              <Mail size={14} className="text-primary" /> Email
            </div>
            <a
              href="mailto:jewokhla@gmail.com"
              data-testid="email-link"
              className="font-plex text-xl md:text-2xl text-foreground hover:text-primary transition-colors duration-300"
            >
              jewokhla@gmail.com
            </a>
          </div>

          <div>
            <div className="flex items-center gap-3 text-muted-foreground font-mono text-[10px] uppercase tracking-widest mb-3">
              <MapPin size={14} className="text-primary" /> Head Office
            </div>
            <p className="font-plex text-lg text-secondary-foreground max-w-sm">
              63 DSIIDC Complex, Okhla Industrial Area Phase 1, New Delhi — 110020
            </p>
          </div>

          <div>
            <div className="flex items-center gap-3 text-muted-foreground font-mono text-[10px] uppercase tracking-widest mb-3">
              <MapPin size={14} className="text-primary" /> Branches
            </div>
            <p className="font-plex text-lg text-secondary-foreground">
              Delhi &nbsp;·&nbsp; Kota
            </p>
          </div>

          <div>
            <div className="flex items-center gap-3 text-muted-foreground font-mono text-[10px] uppercase tracking-widest mb-3">
              <MapPin size={14} className="text-primary" /> Find Us
            </div>
            <div className="border border-border overflow-hidden" data-testid="office-map">
              <iframe
                title="Jagdambay Engineering Works — Okhla office location"
                src="https://www.google.com/maps?q=63+DSIIDC+Complex,+Okhla+Industrial+Area+Phase+1,+New+Delhi+110020&output=embed"
                width="100%"
                height="260"
                style={{ border: 0, filter: "grayscale(0.4) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>

      {/* right: quote form */}
      <div className="px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <h3 className="font-bebas uppercase text-4xl md:text-5xl text-foreground mb-2">
          Request a Quote
        </h3>
        <p className="font-plex text-muted-foreground mb-10">
          Tell us about your project and our team will respond promptly.
        </p>

        <form onSubmit={onSubmit} data-testid="quote-form" className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
          <Field label="Full Name" name="name" value={form.name} onChange={onChange} required testid="quote-name" />
          <Field label="Company" name="company" value={form.company} onChange={onChange} testid="quote-company" />
          <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="quote-email" />
          <Field label="Phone" name="phone" value={form.phone} onChange={onChange} required testid="quote-phone" />

          <div className="flex flex-col sm:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Service Required <span className="text-primary">*</span>
            </label>
            <select
              name="service"
              value={form.service}
              onChange={onChange}
              data-testid="quote-service"
              className="bg-transparent border-0 border-b border-border py-3 text-foreground font-plex text-lg outline-none focus:border-primary transition-colors duration-300"
            >
              {SERVICES.map((s) => (
                <option key={s} value={s} className="bg-background text-foreground">
                  {s}
                </option>
              ))}
            </select>
          </div>

          <Field label="Project Location" name="location" value={form.location} onChange={onChange} testid="quote-location" />
          <div />

          <div className="flex flex-col sm:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Message <span className="text-primary">*</span>
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              required
              rows={3}
              data-testid="quote-message"
              className="bg-transparent border-0 border-b border-border py-3 text-foreground font-plex text-lg outline-none focus:border-primary transition-colors duration-300 resize-none"
            />
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            data-testid="quote-submit"
            whileTap={{ scale: 0.98 }}
            className="sm:col-span-2 group mt-4 inline-flex items-center justify-center gap-3 bg-foreground text-background px-8 py-5 font-mono text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300 disabled:opacity-60"
          >
            {done ? (
              <>
                <Check size={16} /> Request Sent
              </>
            ) : (
              <>
                {loading ? "Sending..." : "Submit Request"}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </>
            )}
          </motion.button>
        </form>
      </div>
    </section>
  );
};
